/**
 * Authoritative, on-chain-sourced PnL for open trades — from sai-keeper's
 * GraphQL API, the same source TradeDetailModal already uses for its "Net
 * P&L (after fees)" figure. One bulk request per trades-list refresh (not
 * per row), so the trades table can stop approximating PnL client-side from
 * a live price tick (which ignores fees entirely and can show sub-cent
 * noise when the price hasn't meaningfully moved) and instead show the same
 * number the contract itself would settle on.
 */

const GRAPHQL_URL = "https://sai-keeper.nibiru.fi/query";

const OPEN_TRADES_PNL_QUERY = `
  query GetOpenTradesPnl($trader: String!) {
    perp {
      trades(where: { trader: $trader, isOpen: true } limit: 200) {
        id
        state { pnlCollateral pnlCollateralAfterFees pnlPct }
      }
    }
  }
`;

/**
 * @param {string} bech32Trader - Nibiru bech32 address (nibi1...) — sai-keeper's
 *   `trader` field expects this, not the EVM 0x address.
 * @returns {Promise<Map<number, {pnlCollateral: number, pnlPct: number}>>}
 *   Keyed by trade id (matches the on-chain trade's user_trade_index as a
 *   plain int — see parseIdx in TradeDetailModal.js for the same mapping).
 *   `pnlCollateral` here is the AFTER-FEES net figure (sai-keeper's
 *   `pnlCollateralAfterFees`), which is what TradeDetailModal headlines as
 *   "Unrealized P&L (after fees)". The raw `pnlCollateral` field is
 *   price-movement-only and is what the modal labels "Price P&L" — using it
 *   here is what made the table disagree with the modal.
 *   Empty Map on any failure — callers should fall back to their own
 *   client-side estimate rather than break the row.
 */
export const fetchOpenTradesPnl = async (bech32Trader) => {
  const byId = new Map();
  if (!bech32Trader) return byId;

  try {
    const res = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ query: OPEN_TRADES_PNL_QUERY, variables: { trader: bech32Trader } }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0]?.message || "GraphQL error");

    const trades = json?.data?.perp?.trades || [];
    for (const t of trades) {
      if (t?.id == null || !t.state) continue;
      // Prefer the after-fees figure so the row matches the modal's headline;
      // fall back to the price-only one if the indexer omits it.
      const net = t.state.pnlCollateralAfterFees ?? t.state.pnlCollateral;
      byId.set(Number(t.id), {
        pnlCollateral: Number(net) / 1e6,
        pnlPct: Number(t.state.pnlPct),
      });
    }
  } catch (err) {
    console.error("fetchOpenTradesPnl failed:", err);
  }

  return byId;
};

/** Extracts the plain trade-index int sai-keeper's `id` matches — mirrors
 *  TradeDetailModal's parseIdx so both stay in sync. */
export const parseTradeIndex = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  const m = String(v).match(/\((\d+)\)/);
  return m ? parseInt(m[1], 10) : parseInt(v, 10);
};
